import { useState } from "react";
import { ChevronDown, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const ITEMS = [
  { key: "driver_files", label: "Driver qualification file for every driver", hint: "Application, MVR, road test or CDL copy, medical card (Part 391)" },
  { key: "drug_program", label: "Drug & alcohol testing program records", hint: "Consortium enrollment, pre-employment negative results, random selection (Part 382)" },
  { key: "clearinghouse", label: "Clearinghouse queries", hint: "Full query before hire and a limited query every year for each driver" },
  { key: "hos_logs", label: "Hours-of-service logs (6 months)", hint: "ELD records or paper logs plus supporting documents (Part 395)" },
  { key: "maintenance", label: "Maintenance file for every vehicle", hint: "Inspection, repair and maintenance records (Part 396)" },
  { key: "annual_inspection", label: "Annual inspection reports", hint: "A current periodic inspection for each truck and trailer (396.17)" },
  { key: "dvir", label: "Driver vehicle inspection reports (DVIRs)", hint: "Post-trip reports and repair sign-offs (396.11)" },
  { key: "insurance_proof", label: "Proof of insurance on file", hint: "Policy and BMC-91/MCS-90 endorsement (Part 387)" },
  { key: "accident_register", label: "Accident register", hint: "Keep one even if you've had no accidents (390.15)" },
  { key: "hazmat_records", label: "Hazmat registration & training records", hint: "PHMSA registration, hazmat employee training, shipping papers", hazmat: true },
];

export default function AuditPrepChecklist({ profile, api }) {
  const hazmat = (profile.cargo_types || []).includes("Hazardous Materials (HAZMAT)");
  const items = ITEMS.filter((i) => !i.hazmat || hazmat);
  const [checked, setChecked] = useState(() => new Set(profile.audit_prep || []));
  const [open, setOpen] = useState(false);
  const done = items.filter((i) => checked.has(i.key)).length;
  const toggle = async (key, on) => {
    const prev = checked;
    const next = new Set(prev);
    if (on) next.add(key); else next.delete(key);
    setChecked(next);
    try {
      await api.put("/profile", { audit_prep: [...next] });
    } catch {
      setChecked(prev);
      toast.error("Could not save. Please try again.");
    }
  };
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-3 border-t border-[#262626] pt-2" data-testid="audit-prep">
      <CollapsibleTrigger data-testid="audit-prep-toggle" className="w-full min-h-[40px] flex items-center justify-between gap-2 text-sm font-semibold hover:text-white transition-colors">
        <span className="inline-flex items-center gap-2"><ClipboardCheck size={15} className="text-[#FFD600]" /> Audit prep checklist</span>
        <span className="inline-flex items-center gap-2">
          <span className={`font-mono-label text-[11px] ${done === items.length ? "text-[#16A34A]" : "text-muted-foreground"}`} data-testid="audit-prep-count">{done}/{items.length}</span>
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </CollapsibleTrigger>
      <div className="h-1 bg-[#262626] mt-1"><div className="h-full bg-[#FFD600] transition-[width] duration-500" style={{ width: `${(done / items.length) * 100}%` }} /></div>
      <CollapsibleContent>
        <ul className="mt-3 space-y-2.5">
          {items.map((i) => (
            <li key={i.key}>
              <label className="flex items-start gap-2.5 cursor-pointer min-h-[40px] py-1" data-testid={`audit-item-${i.key}`}>
                <Checkbox checked={checked.has(i.key)} onCheckedChange={(v) => toggle(i.key, !!v)} className="mt-0.5" data-testid={`audit-check-${i.key}`} />
                <span>
                  <span className={`block text-[13px] leading-snug ${checked.has(i.key) ? "text-muted-foreground line-through" : "text-white"}`}>{i.label}</span>
                  <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">{i.hint}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
